import { useMemoizedFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useExtraTag, useFetch } from '@/app/hooks';
import type { CollectionTag, ElementModality, ElementType, SortByType, TagType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { materialsShareElementsFeed } from '../apis';
import { ELEMENTS_PAGE_SIZE } from '../constants';
import { HEAD_TAB } from '../types';

interface UseShareElementsProps {
  refreshToken: string;
  activeHeadTab: HEAD_TAB;
  setElements: Dispatch<SetStateAction<ElementType[]>>;
  searchValue: string;
  elements: ElementType[];
  allowedModalities?: ElementModality[];
}

export function useShareElements(props: UseShareElementsProps) {
  const { refreshToken, activeHeadTab, setElements, searchValue, elements, allowedModalities } = props;
  const { allTag, likeTag, collectTag, getExtraTagName } = useExtraTag();
  const [activeCategory, setActiveCategory] = useState(allTag.id);
  const [activeCollection, setActiveCollection] = useState<CollectionTag | null>(null);
  const [collections, setCollections] = useState<CollectionTag[]>([]);
  const [categories, setCategories] = useState<TagType[]>();
  const [sortBy, setSortBy] = useState<SortByType>('recommend');
  const tokenRef = useRef('');

  const topicsRef = useRef<string[]>([]);
  const t = useTranslations();

  const updateToken = useCallback((token?: string) => {
    tokenRef.current = token || '';
  }, []);

  const onExpose = useCallback(
    (idx: number) => {
      reportEvent('sharedreference_card_exposure', {
        from_page: 'reference_tool',
        reference_id: elements[idx]?.id || '',
        type_name: categories?.find((it) => it.id === activeCategory)?.name || '',
      });
    },
    [elements, categories, activeCategory],
  );

  const postReferenceListReq = useFetch(materialsShareElementsFeed, {
    manual: true,
    onSuccess: ({ data, error, response }, params) => {
      if (activeHeadTab === HEAD_TAB.SHARE_REFERENCE) {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
        }
        if (data) {
          if (!tokenRef.current && params[0]?.query?.keyword) {
            reportEvent('search_result', {
              is_success: !!data.share_elements?.length,
              position: 'create',
            });
          }
          setElements([
            ...elements,
            ...(data.share_elements || []).map((item) => {
              const { element, share } = item;
              return {
                ...element,
                modality: element?.modality === 'unspecified' || !element?.modality ? 'image' : element?.modality,
                share_meta: {
                  status: element?.share_meta?.status || share?.status,
                },
                tag: share?.operate_tag,
                traceId: response?.headers?.get('x-md-trace-id') || '',
                element,
                share,
              };
            }),
          ]);
          if (data.categories?.length || data.topics?.length) {
            const topics = data.topics || [];
            topicsRef.current = topics.map((it) => it.code || '');
            const categories = [
              allTag, // 全部
              // TODO: 赞过暂不支持筛选，先去掉
              // ...(data.has_liked ? [likeTag] : []), // 赞过
              ...(data.has_collected ? [collectTag] : []), // 收藏过
            ].concat(
              // 插入主题
              topics
                .map((it) => ({
                  name: it.display_name || '',
                  id: it.code || '',
                }))
                .concat(
                  // 插入接口返回的 categories
                  data.categories?.map((it) => ({
                    name: it.display_name || '',
                    id: it.code || '',
                  })) || [],
                ),
            );
            setCategories(categories);
          }
          if (data.tags?.length) {
            setCollections(data.tags.filter((it) => it.type === 'collection'));
          }
          updateToken(data.next_page_token);
        }
      }
    },
  });

  /**
   * 获取社区分享数据
   */
  const getShareReferenceData = useMemoizedFn(() => {
    const isTopic = topicsRef.current.includes(activeCategory);
    const query = {
      'pager.page_token': tokenRef.current || '',
      'pager.pagesz': ELEMENTS_PAGE_SIZE,
      topic: isTopic ? activeCategory : undefined,
      category: isTopic || [allTag.id, likeTag.id, collectTag.id].includes(activeCategory) ? undefined : activeCategory,
      tag: activeCollection?.code,
      is_like: activeCategory === likeTag.id,
      is_collect: activeCategory === collectTag.id,
      sort_by: sortBy,
      ...(searchValue &&
        ![likeTag.id, collectTag.id].includes(activeCategory) && {
          keyword: searchValue,
        }),
      ...(allowedModalities && {
        modalities: allowedModalities,
      }),
    };
    if (activeCollection?.code && activeCollection?.display_name !== searchValue) {
      query.tag = undefined;
      setActiveCollection(null);
    }
    postReferenceListReq.run({
      query,
    });
  });

  useEffect(() => {
    if (activeHeadTab === HEAD_TAB.SHARE_REFERENCE) {
      updateToken('');
      setElements([]);
      getShareReferenceData();
    }

    if (searchValue && [likeTag.id, collectTag.id].includes(activeCategory)) {
      toast({
        message: t('shareReference.search.disabledSearchTip', {
          tag: getExtraTagName(activeCategory) ?? '',
        }),
      });
    }
  }, [activeCategory, sortBy, activeHeadTab, refreshToken, searchValue, allowedModalities]);

  return {
    collections,
    activeCategory,
    activeCollection,
    setActiveCategory,
    setActiveCollection,
    sortBy,
    setSortBy,
    categories,
    getShareReferenceData,
    onExpose,
    postReferenceListReq,
  };
}
